import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { apiRequest, setUnauthenticatedHandler, ApiRequestError } from '../api/client.js';
import type { CurrentUser } from '../api/types.js';
import type { Permission } from '../../shared/permissions.js';

interface SessionState {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  isContractor: boolean;
  isCustomer: boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await apiRequest<CurrentUser>('/api/auth/me');
      setUser(me);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        setUser(null);
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => setUser(null));
    void refresh();
    return () => setUnauthenticatedHandler(null);
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await apiRequest('/api/auth/login', { method: 'POST', body: { email, password } });
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<SessionState>(() => {
    const granted = new Set(user?.permissions ?? []);
    return {
      user,
      loading,
      signIn,
      signOut,
      refresh,
      can: (permission) => granted.has(permission),
      canAny: (...permissions) => permissions.some((permission) => granted.has(permission)),
      isContractor: user?.orgType === 'contractor',
      isCustomer: user?.orgType === 'customer',
    };
  }, [user, loading, signIn, signOut, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within a SessionProvider');
  return context;
}

/**
 * Convenience hook for the authenticated part of the app, where a user is
 * guaranteed to exist by the route guard.
 */
export function useCurrentUser(): CurrentUser {
  const { user } = useSession();
  if (!user) throw new Error('useCurrentUser called outside an authenticated route');
  return user;
}
